import React from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";

import {
  getAuthoritySurfaceRailEntries,
  GUIDED_WORLD_FORGE_SURFACE_ID,
  isProductNavActive,
  type AuthoritySurfaceRailEntry,
  type AuthoritySurfaceRailGate,
  type ProductNavItem,
} from "@/config/productNavigation";
import { ACCESS_PROFILES, resolveAccessProfile } from "@/features/commandDeck/fixtures/accessProfiles";
import type { AccessState } from "@/features/commandDeck/contracts/types";
import { useAuthSession } from "@/features/auth";
import { prefetchOmniCineRoute } from "@/lib/routePrefetch";

function warmAuthorityRoute(item: ProductNavItem) {
  if (item.id === "omnicine") {
    void prefetchOmniCineRoute();
  }
}

function switcherLabel(item: ProductNavItem): string {
  if (item.id === "command_deck") return "Deck";
  if (item.id === "omnicast") return "Cast";
  if (item.id === "omnicine") return "Cine";
  if (item.id === "omnistudio") return "Studio";
  if (item.id === "guided_studio") return "Guided";
  if (item.id === GUIDED_WORLD_FORGE_SURFACE_ID) return "World";
  return item.shortLabel ?? item.label;
}

function statusChipClass(gate: AuthoritySurfaceRailGate, active: boolean): string {
  if (active) {
    return "bg-white/28 text-white";
  }
  switch (gate) {
    case "active":
    case "beta":
      return "bg-emerald-500/15 text-emerald-800";
    case "preview":
      return "bg-violet-500/15 text-violet-800";
    case "coming_soon":
      return "bg-amber-500/15 text-amber-900";
    case "locked":
    default:
      return "bg-slate-500/15 text-slate-600";
  }
}

function useEngineRailAccessState(): AccessState {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { access } = useAuthSession();

  const isCommandDeckPreview =
    location.pathname === "/command-deck/preview" ||
    location.pathname.startsWith("/command-deck/preview/");

  if (isCommandDeckPreview) {
    const profile = resolveAccessProfile(searchParams.get("accessProfile"));
    const profileKey = profile === "revoked" ? "visitor" : profile;
    return ACCESS_PROFILES[profileKey].accessState;
  }

  return access.accessState;
}

type AuthorityRailTabProps = {
  entry: AuthoritySurfaceRailEntry;
  pathname: string;
  variant: "compact" | "vertical";
  onReveal?: () => void;
};

function AuthorityRailTab({ entry, pathname, variant, onReveal }: AuthorityRailTabProps): JSX.Element {
  const { item, gate, statusChip } = entry;
  const active = isProductNavActive(pathname, item);
  const glowHue = item.hue ?? 200;
  const label = switcherLabel(item);
  const title = `${item.label} — ${statusChip}`;

  const compactClass =
    "rounded-full px-2 py-1.5 text-[8px] font-semibold uppercase tracking-[0.14em] transition sm:px-2.5";
  const verticalClass =
    "min-w-[78px] rounded-[15px] px-2 py-2 text-[8px] font-semibold uppercase tracking-[0.16em] transition";
  const baseClass = variant === "compact" ? compactClass : verticalClass;

  const activeStyle = active
    ? {
        background: `linear-gradient(135deg, hsla(${glowHue}, 82%, 58%, ${variant === "compact" ? "0.96" : "0.94"}), hsla(${glowHue + 28}, 82%, 64%, ${variant === "compact" ? "0.88" : "0.84"}))`,
        boxShadow: `0 12px 28px hsla(${glowHue}, 90%, 45%, ${variant === "compact" ? "0.18" : "0.16"})`,
      }
    : undefined;

  return (
    <NavLink
      to={item.path}
      onMouseEnter={() => {
        warmAuthorityRoute(item);
        onReveal?.();
      }}
      onFocus={() => {
        warmAuthorityRoute(item);
        onReveal?.();
      }}
      onPointerDown={() => warmAuthorityRoute(item)}
      className={[
        baseClass,
        "inline-flex flex-col items-center justify-center gap-0.5",
        active ? "text-white" : variant === "compact" ? "text-slate-700 hover:bg-white/46" : "text-slate-700 hover:bg-white/40",
      ].join(" ")}
      style={activeStyle}
      title={title}
      aria-label={title}
    >
      <span>{label}</span>
      <span
        className={[
          "rounded-full px-1 py-px text-[6px] font-semibold uppercase tracking-[0.08em] leading-none",
          statusChipClass(gate, active),
        ].join(" ")}
      >
        {statusChip}
      </span>
    </NavLink>
  );
}

const INACTIVITY_MS = 10000;
const EDGE_TRIGGER_PX = 26;
const CORNER_TRIGGER_PX = 88;
const SWITCHER_LAYER_CLASS = "z-[10020]";
const COMPACT_SWITCHER_QUERY = "(max-width: 640px)";
const SHORT_SWITCHER_QUERY = "(max-height: 360px)";

export default function AuthoritySurfaceSwitcher(): JSX.Element {
  const location = useLocation();
  const accessState = useEngineRailAccessState();
  const entries = React.useMemo(
    () => getAuthoritySurfaceRailEntries(accessState),
    [accessState]
  );
  const pinOpen =
    location.pathname.startsWith("/guided/start-from-world") ||
    location.pathname === "/command-deck" ||
    location.pathname.startsWith("/command-deck/");
  const [hoverCapable, setHoverCapable] = React.useState(false);
  const [compactViewport, setCompactViewport] = React.useState(false);
  const [shortViewport, setShortViewport] = React.useState(false);
  const [open, setOpen] = React.useState(true);
  const [dormant, setDormant] = React.useState(false);
  const idleTimerRef = React.useRef<number | null>(null);

  const armDormancyTimer = React.useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      setDormant(true);
    }, INACTIVITY_MS);
  }, []);

  const revealRail = React.useCallback(() => {
    setOpen(true);
    setDormant(false);
    armDormancyTimer();
  }, [armDormancyTimer]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const media = window.matchMedia("(hover: hover) and (pointer: fine)");
    const syncMode = () => setHoverCapable(media.matches);

    syncMode();
    media.addEventListener("change", syncMode);

    return () => {
      media.removeEventListener("change", syncMode);
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;

    const media = window.matchMedia(COMPACT_SWITCHER_QUERY);
    const syncMode = () => setCompactViewport(media.matches);

    syncMode();

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", syncMode);
      return () => media.removeEventListener("change", syncMode);
    }

    media.addListener(syncMode);
    return () => media.removeListener(syncMode);
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;

    const media = window.matchMedia(SHORT_SWITCHER_QUERY);
    const syncMode = () => setShortViewport(media.matches);

    syncMode();

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", syncMode);
      return () => media.removeEventListener("change", syncMode);
    }

    media.addListener(syncMode);
    return () => media.removeListener(syncMode);
  }, []);

  React.useEffect(() => {
    if (pinOpen) {
      setOpen(true);
      setDormant(false);
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return undefined;
    }

    if (!hoverCapable || compactViewport) {
      setOpen(true);
      setDormant(false);
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return undefined;
    }

    armDormancyTimer();

    const handleMouseMove = (event: MouseEvent) => {
      const nearLeftEdge = event.clientX <= EDGE_TRIGGER_PX;
      const nearCorner = event.clientX <= CORNER_TRIGGER_PX && event.clientY <= CORNER_TRIGGER_PX;

      if (nearLeftEdge || nearCorner) {
        revealRail();
        return;
      }

      if (open) {
        armDormancyTimer();
      }
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [armDormancyTimer, compactViewport, hoverCapable, open, pinOpen, revealRail]);

  const railTransform = pinOpen || open ? "translateX(0)" : "translateX(calc(-100% + 1.2rem))";
  const railOpacity = pinOpen ? 1 : dormant ? 0.34 : 0.94;

  if (shortViewport) {
    return <></>;
  }

  if (!hoverCapable || compactViewport) {
    return (
      <div className={`pointer-events-none fixed inset-x-0 bottom-2 ${SWITCHER_LAYER_CLASS} flex justify-center px-2`}>
        <nav
          aria-label="Authority surfaces"
          className="pointer-events-auto inline-flex max-w-full items-center gap-0.5 rounded-full border border-white/22 bg-[linear-gradient(180deg,rgba(255,255,255,0.78),rgba(224,231,255,0.64))] p-0.5 shadow-[0_14px_32px_rgba(15,23,42,0.12)] backdrop-blur-xl"
        >
          {entries.map((entry) => (
            <AuthorityRailTab
              key={entry.item.id}
              entry={entry}
              pathname={location.pathname}
              variant="compact"
            />
          ))}
        </nav>
      </div>
    );
  }

  return (
    <div className={`pointer-events-none fixed inset-y-0 left-0 ${SWITCHER_LAYER_CLASS}`}>
      <div className="pointer-events-auto absolute left-0 top-0 h-full w-7" onMouseEnter={revealRail} aria-hidden />
      <div className="pointer-events-auto absolute left-0 top-0 h-24 w-24" onMouseEnter={revealRail} aria-hidden />

      <div
        className="absolute left-0 top-[66%] flex -translate-y-1/2 items-center transition-[transform,opacity] duration-300 ease-out"
        style={{ transform: `${railTransform} translateY(-50%)`, opacity: railOpacity }}
        onMouseEnter={revealRail}
      >
        <nav
          aria-label="Authority surfaces"
          className="pointer-events-auto flex items-center gap-1 rounded-r-[22px] border border-white/14 bg-[linear-gradient(180deg,rgba(255,255,255,0.15),rgba(214,228,255,0.07))] px-1 py-1.5 shadow-[0_16px_36px_rgba(15,23,42,0.10)] backdrop-blur-xl"
        >
          <div className="flex flex-col gap-1">
            {entries.map((entry) => (
              <AuthorityRailTab
                key={entry.item.id}
                entry={entry}
                pathname={location.pathname}
                variant="vertical"
                onReveal={revealRail}
              />
            ))}
          </div>

          <button
            type="button"
            onMouseEnter={revealRail}
            onFocus={revealRail}
            className={`flex h-[96px] w-4.5 items-center justify-center rounded-r-full border border-white/16 bg-white/[0.20] text-[8px] font-semibold uppercase tracking-[0.22em] text-slate-600 shadow-[0_8px_18px_rgba(15,23,42,0.10)] ${pinOpen || open ? "" : "motion-safe:animate-[authorityRailPulse_2.6s_ease-in-out_infinite]"}`}
            style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
            aria-label="Reveal surface switcher"
          >
            Surfaces
          </button>
        </nav>
      </div>

      <style>{`
        @keyframes authorityRailPulse {
          0%, 100% { box-shadow: 0 8px 18px rgba(15,23,42,0.10), 0 0 0 rgba(125,211,252,0); }
          50% { box-shadow: 0 10px 22px rgba(15,23,42,0.12), 0 0 18px rgba(125,211,252,0.22); }
        }
      `}</style>
    </div>
  );
}
